/**
 * Client-side decode for basket-api IMAGE option `v1:` wire format.
 * Mirrors basket-api `image-option-decoder` for counting / validation only.
 */

type Batch = { batchId: string; indices: number[] };

export function unpackImageOptionValue(packed: string): Batch[] {
  if (!packed.startsWith('v1:')) {
    throw new Error('INVALID_FORMAT');
  }
  const body = packed.slice(3);
  if (!body) {
    throw new Error('INVALID_FORMAT');
  }
  return body.split(';').map((part) => {
    const colonIdx = part.lastIndexOf(':');
    if (colonIdx === -1) {
      throw new Error('INVALID_FORMAT');
    }
    const token = part.slice(0, colonIdx);
    const maskHex = part.slice(colonIdx + 1);
    if (!/^[0-9a-f]{1,3}$/.test(maskHex)) {
      throw new Error('INVALID_FORMAT');
    }
    const mask = parseInt(maskHex, 16);
    if (mask < 0 || mask > 0x3ff) {
      throw new Error('INVALID_FORMAT');
    }
    const indices: number[] = [];
    for (let i = 0; i < 10; i++) {
      if (mask & (1 << i)) {
        indices.push(i);
      }
    }
    return { batchId: token, indices };
  });
}

/** List HTTPS object URLs from a pre-submit IMAGE value (excludes stored `v1:` tokens). */
export function listHttpsUrlsInImageOptionValue(raw: string | undefined | null): string[] {
  if (raw == null || String(raw).trim() === '') {
    return [];
  }
  const trimmed = String(raw).trim();
  if (trimmed.startsWith('v1:')) {
    return [];
  }
  if (
    (trimmed.startsWith('https://') || trimmed.startsWith('http://')) &&
    !trimmed.startsWith('[')
  ) {
    return [trimmed];
  }
  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (Array.isArray(parsed)) {
      return parsed.map((x) => String(x).trim()).filter(Boolean);
    }
    if (typeof parsed === 'string' && parsed.trim() !== '') {
      return [parsed.trim()];
    }
  } catch {
    return [];
  }
  return [];
}

/**
 * Count images for min/max validation and upload limits.
 * When wire is a packed `v1:` token, the mask can describe batch slots, not just visible
 * thumbnails — prefer `imagePreviewUrls` length from basket when present.
 */
export function countImagesForImageOptionLimit(
  value:
    | {
        imagePreviewUrls?: (string | null)[] | null;
        name?: string;
      }
    | undefined,
): number {
  if (!value) {
    return 0;
  }
  const raw = String(value.name ?? '').trim();
  const listedFromWire = listHttpsUrlsInImageOptionValue(raw);
  if (listedFromWire.length > 0) {
    return listedFromWire.length;
  }

  const previewCount = Array.isArray(value.imagePreviewUrls)
    ? value.imagePreviewUrls.filter((u) => u != null && String(u).trim() !== '').length
    : 0;

  if (raw.startsWith('v1:')) {
    if (previewCount > 0) {
      return previewCount;
    }
    return countImagesInImageOptionValue(raw);
  }

  if (raw === '' && previewCount > 0) {
    return previewCount;
  }

  return countImagesInImageOptionValue(raw);
}

/** Count image slots encoded in an IMAGE option value (URLs JSON, single URL, or v1: token). */
export function countImagesInImageOptionValue(raw: string | undefined | null): number {
  if (raw == null || String(raw).trim() === '') {
    return 0;
  }
  const trimmed = String(raw).trim();
  if (trimmed.startsWith('v1:')) {
    try {
      const batches = unpackImageOptionValue(trimmed);
      return batches.reduce((n, b) => n + b.indices.length, 0);
    } catch {
      return 0;
    }
  }
  if (
    (trimmed.startsWith('https://') || trimmed.startsWith('http://')) &&
    !trimmed.startsWith('[')
  ) {
    return 1;
  }
  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (Array.isArray(parsed)) {
      return parsed.map((x) => String(x).trim()).filter(Boolean).length;
    }
    if (typeof parsed === 'string' && parsed.trim() !== '') {
      return 1;
    }
  } catch {
    return 0;
  }
  return 0;
}
